import { useEffect, useRef, useState } from "react";
import { BarcodeDetector } from "barcode-detector/ponyfill";
import { Flashlight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { beep } from "@/lib/beep";
import BeepVolume from "@/components/BeepVolume";
import { useUsbScanner } from "@/lib/useUsbScanner";

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf"];
const CAM_KEY = "snaplist.scanCamera";
const desktop = () => window.matchMedia?.("(pointer: fine)").matches;

// Webcams are soft and wide: every other frame, also read the guide-box band blown up 2x.
function bandCanvas(v, canvas) {
  const w = v.videoWidth, h = v.videoHeight;
  if (!w || !h) return null;
  const bw = Math.round(w * 0.8), bh = Math.round(h * 0.4);
  canvas.width = bw * 2; canvas.height = bh * 2;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(v, (w - bw) / 2, (h - bh) / 2, bw, bh, 0, 0, bw * 2, bh * 2);
  return canvas;
}

// One camera stream kept warm for a few minutes between scans, so phones don't ask for the camera again on every item.
const KEEP_MS = 3 * 60 * 1000;
const shared = { stream: null, deviceId: "", timer: null };
const live = (s) => s?.getVideoTracks().some((t) => t.readyState === "live");
const stopShared = () => { clearTimeout(shared.timer); shared.stream?.getTracks().forEach((t) => t.stop()); shared.stream = null; };
document.addEventListener("visibilitychange", () => { if (document.hidden && shared.timer) stopShared(); });
async function getStream(deviceId) {
  clearTimeout(shared.timer); shared.timer = null;
  if (live(shared.stream) && (!deviceId || shared.deviceId === deviceId)) return shared.stream;
  stopShared();
  const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
  const ask = (v) => navigator.mediaDevices.getUserMedia({ video: { ...v, ...size }, audio: false });
  const rear = () => ask({ facingMode: { ideal: "environment" } });
  const s = await (deviceId ? ask({ deviceId: { exact: deviceId } }).catch(rear) : rear())
    .catch(() => navigator.mediaDevices.getUserMedia({ video: true, audio: false }));
  shared.stream = s;
  shared.deviceId = s.getVideoTracks()[0]?.getSettings?.().deviceId || deviceId || "";
  if (shared.deviceId) localStorage.setItem(CAM_KEY, shared.deviceId);
  return s;
}
const releaseStream = () => { clearTimeout(shared.timer); shared.timer = setTimeout(stopShared, KEEP_MS); };

function useScanner(open, videoRef, onCode, continuousRef, deviceId, soundRef, qr) {
  const [status, setStatus] = useState("starting");
  const [track, setTrack] = useState(null);
  const [cams, setCams] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let stream, timer, stopped = false, n = 0;
    const detector = new BarcodeDetector({ formats: qr ? [...FORMATS, "qr_code"] : FORMATS });
    const canvas = document.createElement("canvas");
    let last = { code: "", seen: 0 };
    (async () => {
      try {
        stream = await getStream(deviceId);
        if (stopped) { releaseStream(); return; }
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play();
        const t = stream.getVideoTracks()[0];
        if (t.getCapabilities?.().focusMode?.includes("continuous")) t.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
        setTrack(t);
        navigator.mediaDevices.enumerateDevices().then((d) => setCams(d.filter((x) => x.kind === "videoinput"))).catch(() => {});
        setStatus("scanning");
        const tick = async () => {
          if (stopped) return;
          try {
            n += 1;
            const src = n % 2 === 0 ? bandCanvas(v, canvas) : v;
            const codes = v.readyState >= 2 && src ? await detector.detect(src) : [];
            const hit = codes.find((c) => /^\d{8,14}$/.test(c.rawValue)) || codes[0];
            if (hit && !stopped) {
              const now = Date.now();
              // Continuous mode: the same code only counts again once it has left the frame for 1.5s.
              const repeat = hit.rawValue === last.code && now - last.seen < 1500;
              last = { code: hit.rawValue, seen: now };
              if (!repeat) {
                navigator.vibrate?.(60);
                if (soundRef.current) beep(true);
                onCode(hit.rawValue);
                if (!continuousRef.current) return;
              }
            }
          } catch { /* frame not ready */ }
          timer = setTimeout(tick, 120);
        };
        tick();
      } catch {
        setStatus("denied");
      }
    })();
    return () => { stopped = true; clearTimeout(timer); if (videoRef.current) videoRef.current.srcObject = null; if (stream) releaseStream(); setTrack(null); setStatus("starting"); };
  }, [open, videoRef, onCode, deviceId, qr]); // eslint-disable-line react-hooks/exhaustive-deps
  return { status, track, cams };
}

// silent: the caller beeps itself once it knows whether the scan was accepted.
export default function BarcodeScanner({ open, onOpenChange, onDetected, continuous = false, silent = false, qr = false, children }) {
  const videoRef = useRef(null);
  const [manual, setManual] = useState("");
  const [torch, setTorch] = useState(false);
  const onCodeRef = useRef(onDetected);
  onCodeRef.current = onDetected;
  const [onCode] = useState(() => (code) => onCodeRef.current(code));
  const continuousRef = useRef(continuous);
  continuousRef.current = continuous;
  const [deviceId, setDeviceId] = useState(() => localStorage.getItem(CAM_KEY) || "");
  const soundRef = useRef(!silent);
  soundRef.current = !silent;
  const { status, track, cams } = useScanner(open, videoRef, onCode, continuousRef, deviceId, soundRef, qr);
  useUsbScanner(onCode, open, !silent);
  const canTorch = !!track?.getCapabilities?.().torch;
  const pickCam = (id) => { setDeviceId(id); localStorage.setItem(CAM_KEY, id); };
  // Webcams face you: mirror the preview so moving the barcode feels natural (detection reads the raw frame).
  const facing = track?.getSettings?.().facingMode;
  const mirror = facing === "user" || (!facing && desktop());
  const activeId = track?.getSettings?.().deviceId || deviceId;

  const toggleTorch = async () => {
    try { await track.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { /* unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-md flex-col gap-3 overflow-y-auto p-4 sm:p-6" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle>Scan a barcode</DialogTitle>
          <DialogDescription>{desktop() ? "Hold the barcode up to your webcam, 6 to 10 inches away, so it fills the box. Good light helps." : "Point at the UPC, EAN or ISBN. Hold steady about 6 inches away."}</DialogDescription>
        </DialogHeader>
        {cams.length > 1 && (
          <select value={activeId} onChange={(e) => pickCam(e.target.value)} aria-label="Camera" data-testid="barcode-camera-pick"
            className="h-9 w-full shrink-0 rounded-md border border-slate-300 bg-white px-2 text-sm">
            {cams.map((c, i) => <option key={c.deviceId} value={c.deviceId}>{c.label || `Camera ${i + 1}`}</option>)}
          </select>
        )}
        <div className={`relative shrink-0 overflow-hidden rounded-lg bg-slate-900 ${continuous ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
          <video ref={videoRef} playsInline muted className={`h-full w-full object-cover ${mirror ? "-scale-x-100" : ""}`} data-testid="barcode-video" />
          <div className="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-lg border-2 border-white/80 shadow-[0_0_0_999px_rgba(15,23,42,0.35)]">
            <div className="absolute inset-x-3 top-1/2 h-0.5 animate-pulse bg-rose-500" />
          </div>
          {status === "starting" && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-white"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting camera…</div>
          )}
          {status === "denied" && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white" data-testid="barcode-camera-error">
              Camera unavailable. Allow camera access in your browser settings, or type the number below.
            </div>
          )}
          {canTorch && (
            <button type="button" onClick={toggleTorch} aria-label="Toggle flashlight" data-testid="barcode-torch"
              className={`absolute bottom-2 right-2 rounded-full p-2 ${torch ? "bg-amber-400 text-slate-900" : "bg-white/90 text-slate-700"}`}>
              <Flashlight className="h-4 w-4" />
            </button>
          )}
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (manual.trim()) { onDetected(manual.trim()); setManual(""); } }}>
          <Input value={manual} onChange={(e) => setManual(qr ? e.target.value.slice(0, 64) : e.target.value.replace(/[^0-9Xx]/g, ""))} inputMode={qr ? "text" : "numeric"} maxLength={qr ? 64 : 14}
            placeholder={qr ? "Or type the barcode or SKU" : "Or type the barcode number"} className="h-9 text-sm" data-testid="barcode-manual-input" />
          <Button type="submit" size="sm" className="h-9 rounded-full" disabled={!manual.trim()} data-testid="barcode-manual-submit">Search</Button>
        </form>
        <BeepVolume compact />
        {children}
      </DialogContent>
    </Dialog>
  );
}
